import type { PageRef } from '@shared/api';
import type { TaskRow } from '../../state/taskRows';
import type { AgentBarScenario } from './agentBarScenarios';

/**
 * Local-only state used by the AgentBar lab. Each transition below mirrors
 * the visible result of an existing production callback. It never calls an
 * API and is only active while a fixture scenario is selected.
 */
export interface AgentBarPreviewRuntime {
  scenarioId: string;
  rows: TaskRow[];
  sequence: number;
  lastAction: string | null;
}

export type AgentBarPreviewAction =
  | { type: 'send'; text: string }
  | { type: 'answer'; text: string }
  | { type: 'retry'; request: string }
  | { type: 'stop' }
  | { type: 'remove-queued'; rowKey: string }
  | { type: 'dismiss' }
  | { type: 'undo'; rowKey: string };

function cloneRow(row: TaskRow): TaskRow {
  return {
    ...row,
    ask: row.ask ? { ...row.ask, answers: row.ask.answers.map((answer) => ({ ...answer })) } : null,
    summary: [...row.summary],
    status: row.status ? { ...row.status, page: row.status.page ? { ...row.status.page } : undefined } : null,
    page: row.page ? { ...row.page } : null,
  };
}

export function createAgentBarPreviewRuntime(scenario: AgentBarScenario): AgentBarPreviewRuntime {
  return {
    scenarioId: scenario.id,
    rows: scenario.rows.map(cloneRow),
    sequence: 0,
    lastAction: null,
  };
}

function nextKey(runtime: AgentBarPreviewRuntime, kind: 'turn' | 'queue'): string {
  return `agent-bar-preview-${kind}-${runtime.sequence + 1}`;
}

function runningRow(key: string, request: string, page: PageRef | null): TaskRow {
  return {
    key,
    state: 'running',
    request,
    text: '',
    ask: null,
    summary: [],
    undo: false,
    versionAtSend: 7,
    error: null,
    stopped: false,
    activeTool: null,
    status: { label: 'Working…' },
    page,
    kind: null,
    summaryLine: null,
  };
}

function queuedRow(key: string, request: string, page: PageRef | null): TaskRow {
  return {
    ...runningRow(key, request, page),
    state: 'queued',
    versionAtSend: null,
    status: null,
  };
}

function send(runtime: AgentBarPreviewRuntime, text: string): AgentBarPreviewRuntime {
  const request = text.trim();
  if (!request) return runtime;
  const active = runtime.rows.find((row) => row.state === 'running');
  const sequence = runtime.sequence + 1;

  // useChat.send queues only while a turn is literally streaming.
  if (active) {
    return {
      ...runtime,
      sequence,
      rows: [...runtime.rows, queuedRow(nextKey(runtime, 'queue'), request, null)],
      lastAction: 'Queued the request behind the active task.',
    };
  }

  // Sending from waiting, done, or idle starts a fresh assistant turn. The
  // previous primary row drops out because taskRows() renders the latest turn.
  const page = runtime.rows.find((row) => row.state !== 'queued')?.page ?? null;
  const queued = runtime.rows.filter((row) => row.state === 'queued');
  return {
    ...runtime,
    sequence,
    rows: [runningRow(nextKey(runtime, 'turn'), request, page), ...queued],
    lastAction: 'Started a new task with the submitted request.',
  };
}

function dismiss(runtime: AgentBarPreviewRuntime): AgentBarPreviewRuntime {
  const primary = runtime.rows.find((row) => row.state !== 'queued');
  if (!primary) return runtime;
  const queued = runtime.rows.filter((row) => row.state === 'queued');

  // useChat.dismiss starts the next queued request only when Skip dismisses
  // a waiting ask. Ok and the done-row timer simply clear the primary row.
  if (primary.state === 'waiting' && queued.length > 0) {
    const [next, ...rest] = queued;
    const sequence = runtime.sequence + 1;
    return {
      ...runtime,
      sequence,
      rows: [runningRow(nextKey(runtime, 'turn'), next!.request, next!.page), ...rest],
      lastAction: 'Skipped the question and started the next queued request.',
    };
  }

  return {
    ...runtime,
    rows: runtime.rows.filter((row) => row.key !== primary.key),
    lastAction: primary.state === 'waiting' ? 'Skipped the question.' : 'Dismissed the completed result.',
  };
}

export function reduceAgentBarPreview(
  runtime: AgentBarPreviewRuntime,
  action: AgentBarPreviewAction,
): AgentBarPreviewRuntime {
  switch (action.type) {
    case 'send':
      return send(runtime, action.text);
    case 'answer':
      return {
        ...send(runtime, action.text),
        lastAction: 'Submitted the selected answer and started a new task.',
      };
    case 'retry':
      return {
        ...send(runtime, action.request),
        lastAction: 'Retried the failed request as a new task.',
      };
    case 'stop': {
      const active = runtime.rows.find((row) => row.state === 'running');
      if (!active) return runtime;
      return {
        ...runtime,
        rows: runtime.rows
          .filter((row) => row.state !== 'queued')
          .map((row) => row.key === active.key ? {
            ...row,
            state: 'done' as const,
            text: '',
            ask: null,
            summary: [],
            undo: false,
            error: null,
            stopped: true,
            activeTool: null,
            status: null,
            kind: 'stopped' as const,
            summaryLine: 'Stopped.',
          } : row),
        lastAction: 'Stopped the active task and cleared its queued requests.',
      };
    }
    case 'remove-queued':
      return {
        ...runtime,
        rows: runtime.rows.filter((row) => row.key !== action.rowKey),
        lastAction: 'Removed the request from the queue.',
      };
    case 'dismiss':
      return dismiss(runtime);
    case 'undo': {
      const target = runtime.rows.find((row) => row.key === action.rowKey);
      if (!target || target.state !== 'done' || target.versionAtSend == null) return runtime;
      const summaryLine = `Undone. Back to v${target.versionAtSend}.`;
      return {
        ...runtime,
        rows: runtime.rows.map((row) => row.key === action.rowKey ? {
          ...row,
          text: summaryLine,
          summary: [],
          undo: false,
          stopped: false,
          kind: 'change' as const,
          summaryLine,
        } : row),
        lastAction: `Reverted the proposal to v${target.versionAtSend} and marked this turn undone.`,
      };
    }
    default:
      return runtime;
  }
}
